/**
 * Quantity Save Indicator
 * Small inline status icon for per-field/per-row saves: spinner while saving,
 * check on success, alert (with the error message as label/tooltip) on failure.
 * Shared by the recipe ingredient editor and the procurement planner.
 */

import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export type QuantitySaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface QuantitySaveState {
  status: QuantitySaveStatus;
  message?: string;
}

export function QuantitySaveIndicator({ state }: { state: QuantitySaveState }) {
  if (state.status === 'idle') return null;

  const iconClassName = 'h-4 w-4';
  const label = state.status === 'saving'
    ? 'Saving quantity'
    : state.status === 'saved'
      ? 'Quantity saved'
      : state.message || 'Quantity save failed';

  return (
    <span
      role="status"
      aria-label={label}
      title={label}
      className="pointer-events-none absolute inset-y-0 right-2 flex w-4 items-center justify-center"
    >
      {state.status === 'saving' ? (
        <Loader2 className={cn(iconClassName, 'animate-spin text-muted-foreground')} aria-hidden="true" />
      ) : state.status === 'saved' ? (
        <Check className={cn(iconClassName, 'text-green-600')} aria-hidden="true" />
      ) : (
        <AlertCircle className={cn(iconClassName, 'text-red-600')} aria-hidden="true" />
      )}
    </span>
  );
}
